'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import { useState } from 'react';

import {
  GTMEcommerceItem,
  GTMPageView,
  sendGTMEventEcommerce
} from '@/fo/api/gtm';
import { ProductStockStatus } from '@/fo/api/magento';
import {
  selectCountProductInCart,
  selectCountProductInWishlist,
  storeProduct,
  cartActions,
  wishlistActions,
  selectWishlistItemId,
  selectCartItemId,
  selectCartItemLoading
} from '@/fo/store/product';
import {
  UiProductInfoMain,
  UiProductInfoMainProps
} from '@/fo/ui/product/info/main';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { utilEventProductSubscribeModal } from '@/fo/util/event-product-subscribe-modal';
import { MappedProduct } from '@/fo/util/map-product-data';

export interface FeatureProductInfoMainProps
  extends Omit<
    UiProductInfoMainProps,
    'isInWishlist' | 'cartCount' | 'isCartLoading'
  > {
  isAuthorized: boolean;
  goToDescription?: () => void;
  listName: 'product-detail-page' | 'preview';
}

export function FeatureProductInfoMain({
  isAuthorized,
  goToDescription,
  listName,
  ...props
}: FeatureProductInfoMainProps) {
  const router = useRouter();
  const [selectedProduct, setSelectedProduct] = useState(props.product);

  const cartCount = useSelector(
    storeProduct,
    selectCountProductInCart(selectedProduct.sku)
  );
  const cartItemId = useSelector(
    storeProduct,
    selectCartItemId(selectedProduct.sku)
  );
  const cartLoading = useSelector(
    storeProduct,
    selectCartItemLoading(selectedProduct.sku)
  );
  const wishlistCount = useSelector(
    storeProduct,
    selectCountProductInWishlist(
      selectedProduct.parentSku || selectedProduct.sku
    )
  );
  const wishlistItemId = useSelector(
    storeProduct,
    selectWishlistItemId(selectedProduct.parentSku || selectedProduct.sku)
  );

  const isInWishlist = wishlistCount > 0;
  const isInCart = cartCount > 0;

  const positionIndex =
    props.product.configurableVariants?.findIndex(
      (item) => item.sku === selectedProduct.sku
    ) ?? 0;

  const itemGtmData: GTMEcommerceItem = {
    item_id: selectedProduct.sku,
    item_name: selectedProduct.name,
    price: String(
      selectedProduct.finalPrice ?? selectedProduct.basePrice ?? ''
    ),
    item_brand: selectedProduct.brand,
    item_category: selectedProduct.series || selectedProduct.brand || '',
    variant: selectedProduct.volume ?? '',
    item_list_name: listName,
    position: positionIndex + 1,
    quantity: 1
  };

  const onCartClick = () => {
    if (selectedProduct.stockStatus === ProductStockStatus.OutOfStock) {
      utilEventProductSubscribeModal.open(selectedProduct.mindboxId);
      return;
    }

    if (isInCart) {
      utilEventConfigurableProduct.close();
      router.push('/cart');
    } else {
      cartActions.addProductsToCart([
        {
          sku: selectedProduct.sku,
          quantity: 1,
          product: selectedProduct
        }
      ]);
    }
  };

  const onCartChange = async (quantity: number) => {
    if (!cartItemId) return;

    if (quantity === 0) {
      await cartActions.removeCartItem(
        selectedProduct.sku,
        cartItemId,
        selectedProduct
      );
    } else {
      await cartActions.updateCartItem(
        selectedProduct.sku,
        cartItemId,
        quantity,
        selectedProduct
      );
    }
  };

  const onWishlistClick = () => {
    if (isAuthorized) {
      if (isInWishlist && wishlistItemId) {
        wishlistActions.removeWishlistItems([
          {
            sku: selectedProduct.parentSku || selectedProduct.sku,
            wishlistItemId
          }
        ]);
      } else {
        sendGTMEventEcommerce({
          event: 'add_to_wishlist',
          ecommerce: {
            value: itemGtmData.price,
            items: [itemGtmData]
          }
        });
        wishlistActions.addProductToWishlist(
          selectedProduct.sku,
          1,
          selectedProduct.parentSku
        );
      }
    } else {
      utilEventAuthModal.open('wishlist-button');
    }
  };

  const onOptionChange = (product: MappedProduct) => {
    setSelectedProduct(product);
  };

  const price = String(
    selectedProduct.finalPrice ?? selectedProduct.basePrice ?? ''
  );

  return (
    <>
      <GTMPageView
        key={selectedProduct.sku}
        event="view_item"
        value={price}
        items={[
          {
            item_id: selectedProduct.sku,
            item_name: selectedProduct.name,
            price,
            item_brand: selectedProduct.brand,
            item_category:
              selectedProduct.series || selectedProduct.brand || '',
            item_list_name: listName,
            variant: selectedProduct.volume ?? '',
            position: positionIndex + 1
          }
        ]}
      />
      <UiProductInfoMain
        {...props}
        goToDescription={goToDescription}
        isAuthorized={isAuthorized}
        isInWishlist={wishlistCount > 0}
        isCartLoading={cartLoading}
        cartCount={cartCount}
        onCartClick={onCartClick}
        onCartChange={onCartChange}
        onWishlistClick={onWishlistClick}
        onOptionChange={onOptionChange}
      />
    </>
  );
}
